import React, { useState, useMemo, memo, useCallback } from "react";
import { motion } from "framer-motion";
import { Container, Row, Col } from "react-bootstrap";
import "../styles/Projects.css";
import useScrollAnimation from "../hooks/useScrollAnimation";

import project1 from "../../src/assets/project1.webp";
import project2 from "../../src/assets/project5.webp";
import project3 from "../../src/assets/project3.webp";
import project4 from "../../src/assets/project4.webp";
import project5 from "../../src/assets/project2.webp";
import project6 from "../../src/assets/project6.webp";
import {
  SiJavascript,
  SiNextdotjs,
  SiHtml5,
  SiTailwindcss,
  SiReact,
  SiCss3,
  SiTypescript,
  SiBootstrap,
} from "react-icons/si";
import { HiArrowNarrowRight } from "react-icons/hi";

const PROJECTS_DATA = [
  {
    id: 1,
    title: "Project One",
    image: project1,
    description:
      "A dynamic restaurant website built with Next.js, featuring SEO-optimized pages, a sleek UI, and secure user authentication via Firebase. The site offers fast navigation and a visually engaging experience for visitors looking to explore menus and offers.",
    repoLink: "https://github.com/RehamRaed/resturant.git",
    demoLink: "https://restaurant-online-ten.vercel.app/",
    icons: [
      { Icon: SiJavascript, size: 25, color: "#f7df1e" },
      { Icon: SiNextdotjs, size: 25, color: "#000000" }, 
      { Icon: SiHtml5, size: 25, color: "#e34f26" }, 
      { Icon: SiTailwindcss, size: 25, color: "#06b6d4" },
      { Icon: SiReact, size: 25, color: "#61dafb" }, 
    ],
  },
  {
    id: 2,
    title: "Project Two",
    image: project2,
    description:
      "A University accommodation booking platform designed for international students in Egypt. Developed with Next.js and TypeScript, it offers a user-friendly interface, fast performance, and a smooth booking process for dorms and apartments.",
    repoLink: "https://github.com/RehamRaed/CampusNest.git",
    demoLink: "https://campus-nest-beta.vercel.app/",
    icons: [
      { Icon: SiNextdotjs, size: 20, color: "#000000" }, 
      { Icon: SiTypescript, size: 20, color: "#3178c6" }, 
      { Icon: SiReact, size: 20, color: "#61dafb" }, 
      { Icon: SiHtml5, size: 20, color: "#e34f26" }, 
      { Icon: SiTailwindcss, size: 20, color: "#06b6d4" }, 
    ],
  },
  {
    id: 3,
    title: "Project Three",
    image: project3,
    description:
      "A feature-rich e-commerce store for shoes, developed using React.js and Bootstrap. Users can browse by categories, filter products, view quick modals for product previews, and read testimonials, all wrapped in a polished and intuitive interface.",
    repoLink: "https://github.com/RehamRaed/Online_Shoes_Shop.git",
    demoLink: "https://online-shoes-shop.vercel.app/",
    icons: [
      { Icon: SiReact, size: 20, color: "#61dafb" },
      { Icon: SiJavascript, size: 20, color: "#f7df1e" },
      { Icon: SiHtml5, size: 20, color: "#e34f26" },
      { Icon: SiCss3, size: 20, color: "#1572B6" },
      { Icon: SiBootstrap, size: 20, color: "#563D7C" },
    ],
  },
  {
    id: 4,
    title: "Project Four",
    image: project4,
    description:
      "A vibrant recipe web application that curates a diverse collection of recipes from different cuisines. Built with React.js and Firebase, it offers seamless exploration of dishes, with an adaptive layout optimized for all devices.",
    repoLink: "https://github.com/RehamRaed/Recipes_Website.git",
    demoLink: "https://your-recipesguide.vercel.app/",
    icons: [
      { Icon: SiReact, size: 20, color: "#61dafb" },
      { Icon: SiJavascript, size: 20, color: "#f7df1e" },
      { Icon: SiHtml5, size: 20, color: "#e34f26" },
      { Icon: SiCss3, size: 20, color: "#1572B6" },
      { Icon: SiBootstrap, size: 20, color: "#563D7C" },
    ],
  },
  
  {
    id: 5,
    title: "Project Five",
    image: project5,
    description:
      "An interactive movie platform created with React.js and JSON data, allowing users to browse trending, top-rated, and upcoming films. It includes a smooth browsing experience with user authentication and personalized content features.",
    repoLink: "https://github.com/RehamRaed/movie-explorer.git",
    demoLink: "https://movies-website-ashy.vercel.app/",
    icons: [
      { Icon: SiReact, size: 20, color: "#61dafb" },
      { Icon: SiJavascript, size: 20, color: "#f7df1e" }, 
      { Icon: SiHtml5, size: 20, color: "#e34f26" }, 
      { Icon: SiCss3, size: 20, color: "#1572B6" },
    ],
  }
  ,
  {
    id: 6,
    title: "Project Six",
    image: project6,
    description:
      "A dedicated university landing page aimed at enhancing student engagement. Built with React.js, it features an organized component-based structure for easy scalability, and presents academic information through a clean, approachable design.",
    repoLink: "https://github.com/RehamRaed/University_Website.git",
    demoLink: "https://university-sigma-eight.vercel.app/",
    icons: [
      { Icon: SiReact, size: 20, color: "#61dafb" },
      { Icon: SiJavascript, size: 20, color: "#f7df1e" },
      { Icon: SiHtml5, size: 20, color: "#e34f26" },
      { Icon: SiCss3, size: 20, color: "#1572B6" },
    ],
  },
];

const iconContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const iconVariants = {
  hidden: { opacity: 0, x: -20, scale: 1, transformOrigin: "center" },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transformOrigin: "center",
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const ProjectCard = memo(function ProjectCard({ project, isHovered, onMouseEnter, onMouseLeave }) {
  return (
    <Col md={6} sm={12}>
      <div
        className="project-card"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="image-container">
          <div
            className="background-image"
            style={{
              backgroundImage: `url(${project.image})`,
            }}
          />

          <div className={`hover-content ${isHovered ? "show" : ""}`}>
            <div className="links">
              <a
                href={project.repoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="pulse-btn"
                aria-label={`View ${project.title} source code on GitHub`}
              >
                GitHub
              </a>
              <a
                href={project.demoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="pulse-btn"
                aria-label={`View ${project.title} live demo`}
              >
                Demo
              </a>
            </div>
            <motion.div
              className="icons"
              variants={iconContainerVariants}
              initial="hidden"
              animate={isHovered ? "visible" : "hidden"}
              style={{ display: "flex", gap: "12px" }}
            >
              {project.icons.map(({ Icon, size, color }, i) => (
                <motion.div
                  key={i}
                  variants={iconVariants}
                  style={{
                    padding: "5px",
                    fontSize: "1.2rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "visible",
                  }}
                >
                  <Icon size={size} color={color} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        <div className="card-description">{project.description}</div>
      </div>
    </Col>
  );
});

export default function Projects() {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  const [titleRef, animate] = useScrollAnimation();

  const title = "My Projects";

  const animatedTitle = useMemo(
    () =>
      title.split("").map((char, i) => (
        <span
          key={i}
          style={{ animationDelay: `${i * 0.08}s` }}
          className="animated-char"
        >
          {char === " " ? "\u00A0" : char}
        </span>
      )),
    [title]
  );

  const handleMouseEnter = useCallback((index) => {
    setHoveredIndex(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  return (
    <section id="projects" className="projects-section">
      <Container className="projects-container">
        <h2
          ref={titleRef}
          className={`animated-title ${animate ? "show" : ""}`}
          style={{ marginBottom: "2rem" }}
        >
          {animatedTitle}
        </h2>

        <Row className="g-4">
          {PROJECTS_DATA.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              isHovered={hoveredIndex === index}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            />
          ))}
        </Row>
        <a
          href="https://github.com/RehamRaed"
          target="_blank"
          rel="noopener noreferrer"
          className="border-gradient-btn"
        >
          Show All Projects <HiArrowNarrowRight size={20} color="#fff" />
        </a>
      </Container>
    </section>
  );
}
